import clsx from "clsx";
import type { Customer360Snapshot } from "../../../types/crm";
import { useRecomputeHealth } from "../../../helpers/hooks/use-recompute-health";
import { HealthBandChip } from "../HealthBandChip";
import { HealthBreakdownPopover } from "../HealthBreakdownPopover";

interface Props {
  customerId: string;
  healthScore: Customer360Snapshot["healthScore"];
}

const SEGMENT_LABEL: Record<string, string> = {
  ramp_up: "Ramp-Up",
  stabilized: "Stabilized",
};

/**
 * 360 page: health score section.
 *
 * Renders a prominent chip (score + band) alongside the full 6-signal
 * breakdown inline. Same component used by the dashboard chip, but rendered
 * in-flow (not hover-popover) on the 360 page.
 */
export function HealthSection({ customerId, healthScore }: Props) {
  const recompute = useRecomputeHealth(customerId);

  const recomputeButton = (
    <button
      onClick={() => recompute.mutate()}
      disabled={recompute.isPending}
      className="text-xs font-medium text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {recompute.isPending ? "Recomputing…" : "Recompute"}
    </button>
  );

  if (!healthScore) {
    return (
      <section className="bg-white rounded-xl border border-border-secondary shadow-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border-secondary">
          <h2 className="text-sm font-semibold text-content-primary">
            Health
          </h2>
          {recomputeButton}
        </div>
        <div className="p-4 text-xs text-content-tertiary italic">
          Not yet computed.
        </div>
      </section>
    );
  }

  const segmentLabel = healthScore.segment
    ? SEGMENT_LABEL[healthScore.segment]
    : null;
  const segmentCls =
    healthScore.segment === "ramp_up"
      ? "bg-blue-100 text-blue-800"
      : "bg-gray-100 text-gray-700";

  return (
    <section className="bg-white rounded-xl border border-border-secondary shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-secondary">
        <h2 className="text-sm font-semibold text-content-primary">Health</h2>
        <div className="flex items-center gap-2">
          {segmentLabel && (
            <span
              className={clsx(
                "text-[10px] font-medium px-2 py-0.5 rounded-full uppercase tracking-wider",
                segmentCls,
              )}
            >
              {segmentLabel}
            </span>
          )}
          {recomputeButton}
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <HealthBandChip
            band={healthScore.band}
            score={healthScore.score}
            className="text-sm px-2.5 py-1"
          />
          {healthScore.computedAt && (
            <span className="text-[11px] text-content-tertiary">
              {new Date(healthScore.computedAt).toLocaleDateString("en-US", {
                timeZone: "America/New_York",
                month: "short",
                day: "numeric",
              })}
            </span>
          )}
        </div>
        <HealthBreakdownPopover
          breakdown={healthScore.componentBreakdown}
          variant="inline"
        />
      </div>
    </section>
  );
}
